<template>
  <MetaInfo :title="meta.title" :description="meta.description"></MetaInfo>
  <navigation></navigation>

  <PageHeader
    title="Videos"
    subtitle-text="Guides, product walkthroughs and webinars to help you get more from your marketing with Transpond."
    class="videos__header"
  >
    <a
      :href="channelUrl"
      class="btn btn--secondary videos__subscribe mt-6"
      target="_blank"
      rel="noopener"
    >
      <svg class="btn__icon btn__icon--before" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M23.5 6.2a3.04 3.04 0 0 0-2.14-2.14C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.36.46A3.04 3.04 0 0 0 .5 6.2 31.7 31.7 0 0 0 0 12a31.7 31.7 0 0 0 .5 5.8 3.04 3.04 0 0 0 2.14 2.14C4.5 20.4 12 20.4 12 20.4s7.5 0 9.36-.46a3.04 3.04 0 0 0 2.14-2.14A31.7 31.7 0 0 0 24 12a31.7 31.7 0 0 0-.5-5.8zM9.75 15.57V8.43L15.84 12l-6.09 3.57z"/>
      </svg>
      Subscribe on YouTube
    </a>
  </PageHeader>

  <!-- Featured video -->
  <section class="videos__featured">
    <div class="videos__container">
      <button
        class="videos__featured-card"
        type="button"
        v-on:click="openVideo(featuredVideo)"
      >
        <div class="videos__thumb videos__thumb--featured">
          <img
            :src="thumbnail(featuredVideo)"
            :alt="featuredVideo.title + ' video thumbnail'"
          />
          <span class="videos__play videos__play--lg" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.53.85l10.2-6.5a1 1 0 0 0 0-1.7L9.53 4.65A1 1 0 0 0 8 5.5z"/></svg>
          </span>
          <span class="videos__duration">{{ featuredVideo.duration }}</span>
        </div>
        <div class="videos__featured-content">
          <span class="videos__label">Featured</span>
          <h2 class="h3 mb-4">{{ featuredVideo.title }}</h2>
          <p class="videos__featured-description mb-8">
            {{ featuredVideo.description }}
          </p>
          <span class="btn btn--primary">Watch now</span>
        </div>
      </button>
    </div>
  </section>

  <!-- Filters + grid -->
  <section class="videos__library">
    <div class="videos__container">
      <div class="videos__toolbar">
        <ul class="videos__filters" aria-label="Filter videos">
          <li v-for="category in categories" :key="category">
            <button
              type="button"
              class="videos__filter"
              :class="{ 'videos__filter--active': activeCategory === category }"
              :aria-pressed="activeCategory === category"
              v-on:click="activeCategory = category"
            >
              {{ category }}
            </button>
          </li>
        </ul>

        <label class="videos__search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Search videos"
            aria-label="Search videos"
          />
        </label>
      </div>

      <div v-if="filteredVideos.length" class="videos__grid">
        <button
          v-for="video in filteredVideos"
          :key="video.id"
          type="button"
          class="videos__card"
          v-on:click="openVideo(video)"
        >
          <div class="videos__thumb">
            <img :src="thumbnail(video)" :alt="video.title + ' video thumbnail'" loading="lazy" />
            <span class="videos__play" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.53.85l10.2-6.5a1 1 0 0 0 0-1.7L9.53 4.65A1 1 0 0 0 8 5.5z"/></svg>
            </span>
            <span class="videos__duration">{{ video.duration }}</span>
          </div>
          <span class="videos__category">{{ video.category }}</span>
          <h5 class="videos__card-title">{{ video.title }}</h5>
          <p class="videos__card-description">{{ video.description }}</p>
        </button>
      </div>

      <p v-else class="videos__empty">
        No videos match "{{ searchTerm }}". Try a different search or category.
      </p>
    </div>
  </section>

  <!-- Webinar promo -->
  <section class="videos__promo">
    <div class="videos__container">
      <div class="videos__promo-content">
        <div>
          <h3 class="mb-3">Want a guided tour?</h3>
          <p class="videos__promo-text">
            Book a live demo and we'll walk you through Transpond, tailored
            to the way your business works.
          </p>
        </div>
        <router-link to="/contactus" class="btn btn--primary">Book a demo</router-link>
      </div>
    </div>
  </section>

  <FooterCTA></FooterCTA>
  <site-footer></site-footer>

  <!-- Player modal -->
  <div
    v-if="activeVideo"
    class="videos__modal"
    role="dialog"
    aria-modal="true"
    :aria-label="activeVideo.title"
    v-on:click.self="closeVideo"
  >
    <div class="videos__modal-inner">
      <button
        ref="closeButton"
        type="button"
        class="videos__modal-close"
        aria-label="Close video"
        v-on:click="closeVideo"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
      <div class="videos__player">
        <iframe
          :src="embedUrl(activeVideo)"
          :title="activeVideo.title"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
      </div>
      <div class="videos__modal-meta">
        <span class="videos__category">{{ activeVideo.category }} · {{ activeVideo.duration }}</span>
        <h4 class="mb-2">{{ activeVideo.title }}</h4>
        <p>{{ activeVideo.description }}</p>
        <a
          :href="watchUrl(activeVideo)"
          class="videos__watch-link"
          target="_blank"
          rel="noopener"
        >Watch on YouTube</a>
      </div>
    </div>
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import PageHeader from "@/components/Header/PageHeader";
import FooterCTA from "@/components/FooterCTA";
import SiteFooter from "@/components/SiteFooter";

// Videos from the Transpond and Capsule CRM YouTube channels, newest first.
// The first one is featured. Thumbnails and embeds come straight from YouTube by id.
const videos = [
  {
    id: "syeYzsnnl_k",
    title: "Why Transpond is the Mailchimp alternative you've been looking for",
    description:
      "Tired of exporting lists and switching between tools? See how Transpond connects your CRM and marketing in one simpler, cheaper platform.",
    category: "Marketing platform comparisons",
    duration: "7:43",
  },
  {
    id: "GJqLhEcSnKk",
    title: "Transpond vs MailerLite: Which marketing platform is best for you?",
    description:
      "We compare pricing, features, integrations, ease of use and support to help you choose the right platform for your small business.",
    category: "Marketing platform comparisons",
    duration: "9:52",
  },
  {
    id: "TquNgmDmQeM",
    title: "Email marketing templates: How to create professional emails in minutes",
    description:
      "How templates save time, keep your emails consistent and help you build more effective campaigns in Transpond.",
    category: "Email marketing",
    duration: "9:52",
  },
  {
    id: "BKV_xEryKuA",
    title: "Customer retention strategies: 6 ways to keep customers coming back",
    description:
      "Six practical retention strategies, with examples of how to use Transpond to keep customers engaged long after their first purchase.",
    category: "Marketing strategy",
    duration: "5:49",
  },
  {
    id: "gZsAM8O3t3c",
    title: "Email automation for small businesses: 5 automations you should be using",
    description:
      "Five essential email automations, with step-by-step guidance on setting them up in Transpond to save time and nurture leads.",
    category: "Email marketing",
    duration: "6:48",
  },
  {
    id: "EGQALahUh0w",
    title: "Marketing automation: 5 automations to set up first",
    description:
      "What marketing automation is, practical examples, and the five automations to set up first, from welcome emails to appointment reminders.",
    category: "Marketing strategy",
    duration: "6:54",
  },
  {
    id: "ct68uV0A0n0",
    title: "Transpond SMS overview",
    description:
      "Where to find SMS campaigns in Transpond, which plans include them, and how to create your first text message campaign.",
    category: "SMS & social",
    duration: "3:14",
  },
  {
    id: "xTMN6hMi5Tg",
    title: "How to use Transpond to manage your full marketing mix",
    description:
      "Use new channels alongside your campaigns, turn website activity into conversions, and answer sales and support queries automatically with Conversations.",
    category: "Webinars",
    duration: "52:02",
  },
  {
    id: "DThElVCi8RI",
    title: "How the Capsule team uses Transpond to deliver their marketing strategy",
    description:
      "Our marketing and customer success teams share how they manage customer comms, deliver campaigns and stay consistent on social media.",
    category: "Webinars",
    duration: "46:18",
  },
];

// CookieYes blocks youtube.com embeds unless both of these categories are
// accepted, so they must match the YouTube rule in the CookieYes dashboard.
const YOUTUBE_COOKIE_CATEGORIES = ["functional", "advertisement"];

function youtubeCookiesAccepted() {
  const consent = window.getCkyConsent?.();
  return YOUTUBE_COOKIE_CATEGORIES.every(
    (category) => consent?.categories?.[category] === true
  );
}

const categories = [
  "Email marketing",
  "Marketing strategy",
  "SMS & social",
  "Marketing platform comparisons",
  "Platform",
  "Webinars",
];

export default {
  name: "VideosPage",
  components: {
    MetaInfo,
    Navigation,
    PageHeader,
    FooterCTA,
    SiteFooter,
  },
  data() {
    return {
      videos,
      activeCategory: "All",
      searchTerm: "",
      activeVideo: null,
      channelUrl: "https://www.youtube.com/@TranspondMarketing",
      meta: {
        title: "Videos - Transpond",
        description:
          "Guides, product walkthroughs and webinars on email, SMS, social and marketing strategy, plus help choosing the right marketing platform for your business.",
      },
    };
  },
  computed: {
    featuredVideo() {
      return this.videos[0];
    },
    categories() {
      // Only show filters that have at least one video in them.
      const used = categories.filter((category) =>
        this.videos.some((video) => video.category === category)
      );
      return ["All", ...used];
    },
    filteredVideos() {
      const term = this.searchTerm.trim().toLowerCase();
      const showAll = this.activeCategory === "All";
      return this.videos.filter((video) => {
        // The featured video sits above the grid, so only repeat it once the
        // list has been narrowed down.
        if (showAll && !term && video === this.featuredVideo) return false;
        if (!showAll && video.category !== this.activeCategory) return false;
        if (!term) return true;
        return (video.title + " " + video.description + " " + video.category)
          .toLowerCase()
          .includes(term);
      });
    },
  },
  methods: {
    thumbnail(video) {
      return `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`;
    },
    // YouTube's bot checks often refuse playback in the cookieless
    // youtube-nocookie.com player. Use the standard player whenever the
    // visitor has accepted YouTube cookies.
    embedUrl(video) {
      const host = youtubeCookiesAccepted()
        ? "www.youtube.com"
        : "www.youtube-nocookie.com";
      return `https://${host}/embed/${video.id}?autoplay=1&rel=0&playsinline=1`;
    },
    watchUrl(video) {
      return `https://www.youtube.com/watch?v=${video.id}`;
    },
    openVideo(video) {
      this.activeVideo = video;
      document.body.classList.add("videos-modal-open");
      document.addEventListener("keydown", this.onKeydown);
      this.$nextTick(() => {
        this.$refs.closeButton?.focus();
      });
    },
    closeVideo() {
      this.activeVideo = null;
      document.body.classList.remove("videos-modal-open");
      document.removeEventListener("keydown", this.onKeydown);
    },
    onKeydown(event) {
      if (event.key === "Escape") this.closeVideo();
    },
  },
  beforeUnmount() {
    document.body.classList.remove("videos-modal-open");
    document.removeEventListener("keydown", this.onKeydown);
  },
};
</script>
